import { Box, SxProps, Theme, Typography } from '@mui/material';
import { Tooltip } from 'src/components/Tooltip/Tooltip';
import { useHoverCardDelay } from 'src/utils/hooks/use-hover-card-delay';
import { PartnerBusinessHoursCard } from './partner-business-hours-card';
import {
  partnerStatusHeadsUpSx,
  partnerStatusLabelSx,
  partnerStatusLineSx,
} from './partner-business-status.styles';
import { buildHeadsUpLabel, buildStatusLabel } from './partner-business-status.utils';
import { usePartnerBusinessStatus } from './use-partner-business-status';

// The theme's MuiTooltip overrides draw their own background, border and shadow;
// strip them so the hours card is the only layer with chrome.
const BARE_TOOLTIP_SLOT_SX: SxProps<Theme> = {
  padding: 0,
  margin: '0.375rem 0 0',
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  maxWidth: 'none',
};

interface PartnerBusinessStatusLineProps {
  partnerId?: number | null;
  timeZone?: string | null;
}

export const PartnerBusinessStatusLine = ({
  partnerId,
  timeZone,
}: PartnerBusinessStatusLineProps) => {
  const status = usePartnerBusinessStatus(partnerId, timeZone);
  const { isOpen, openNow, openSoon, closeNow, closeSoon } = useHoverCardDelay();

  if (!status.isLoaded || !status.weeklyHours.length) return null;

  const headsUp = buildHeadsUpLabel(status);

  return (
    <Tooltip
      placement="bottom-start"
      open={isOpen}
      onClose={closeNow}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      nonInteractable={false}
      slotProps={{
        popper: {
          sx: { pointerEvents: 'auto' },
        },
        tooltip: {
          onMouseEnter: openNow,
          onMouseLeave: closeSoon,
          sx: BARE_TOOLTIP_SLOT_SX,
        },
      }}
      title={<PartnerBusinessHoursCard status={status} />}
    >
      <Typography
        component="span"
        sx={partnerStatusLineSx}
        data-testid="partner-business-status"
        onMouseEnter={openSoon}
        onMouseLeave={closeSoon}
        onFocus={openSoon}
        onBlur={closeSoon}
      >
        <Box component="span" sx={partnerStatusLabelSx(status.state)}>
          {buildStatusLabel(status)}
        </Box>
        {headsUp ? (
          <Box component="span" sx={partnerStatusHeadsUpSx}>
            {` · ${headsUp}`}
          </Box>
        ) : null}
      </Typography>
    </Tooltip>
  );
};
